import { StrictMode } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import App from './App';
import { UpdateBanner } from '@components/shared/UpdateBanner';
import './i18n';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found');
}

const root = createRoot(container);
root.render(
  <StrictMode>
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
      </ThemeProvider>
    </BrowserRouter>
  </StrictMode>
);

// ── Service worker registration with update flow ────────────────────────────
//
// The UpdateBanner is mounted via an INDEPENDENT createRoot into a
// runtime-created div (NOT inside <App/> — App.tsx is outside this file's
// ownership and the banner must outlive route changes). Registration and
// rendering live in the same file, so visibility and callbacks are plain
// props — no event bus or module-level subscriber.

let updateBannerRoot: Root | null = null;
let updateBannerContainer: HTMLDivElement | null = null;

function removeUpdateBanner() {
  const root = updateBannerRoot;
  const container = updateBannerContainer;
  updateBannerRoot = null;
  updateBannerContainer = null;
  if (root) {
    // Deferred: React 18 errors when a root is unmounted synchronously from
    // inside one of its own event handlers ("Attempted to synchronously
    // unmount a root while React was already rendering").
    setTimeout(() => {
      root.unmount();
      container?.remove();
    }, 0);
  } else {
    container?.remove();
  }
}

function showUpdateBanner(registration: ServiceWorkerRegistration) {
  if (updateBannerRoot) return; // already showing

  updateBannerContainer = document.createElement('div');
  document.body.appendChild(updateBannerContainer);
  updateBannerRoot = createRoot(updateBannerContainer);
  updateBannerRoot.render(
    <ThemeProvider theme={theme}>
      <UpdateBanner
        onReload={() => {
          // Clean up the banner root BEFORE triggering the reload path, then
          // tell the waiting SW to activate; the controllerchange listener
          // below performs the actual (single) reload.
          removeUpdateBanner();
          registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
        }}
        onDismiss={removeUpdateBanner}
      />
    </ThemeProvider>
  );
}

// Register service worker for offline support
if ('serviceWorker' in navigator) {
  // Reload exactly once when the new SW takes control after SKIP_WAITING.
  // The one-shot flag protects against reload loops; sw.ts does not call
  // clientsClaim(), so controllerchange does not fire on first install.
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        // A new version may already be installed and waiting (e.g. the tab
        // was reopened after an update finished installing in the background).
        if (registration.waiting && navigator.serviceWorker.controller) {
          showUpdateBanner(registration);
        }

        registration.onupdatefound = () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.onstatechange = () => {
            // 'installed' + an existing controller means a NEW version is
            // waiting (without a controller it is the very first install —
            // nothing to update).
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              showUpdateBanner(registration);
            }
          };
        };
      })
      .catch((error) => {
        console.error('SW registration failed:', error);
      });
  });
}
